import { TextButton } from '../../elements/form-elements'
import React, { useEffect, useState } from 'react'

import { registerPushToken, unregisterPushToken } from '../../async/push'
import { VBox } from '../../elements/basic-elements'
import {
  UserNotificationPrivacySettings,
  UserNotificationSettings
} from '../../types/notifications'
import FormSwitch from '../form-switch'
import { SettingsSection } from '../settings/settings-section'
import NotificationDebugPanel from './notification-debug-panel'
import PushBlockedModal from './push-blocked-modal'

export const UserNotificationEdit = ({
  userId,
  settings,
  notificationPrivacy,
  onUpdate,
  onUpdatePrivacy
}: {
  userId?: string
  settings?: UserNotificationSettings
  notificationPrivacy?: UserNotificationPrivacySettings
  onUpdate: (notifications: UserNotificationSettings) => void
  onUpdatePrivacy: (notificationPrivacy: UserNotificationPrivacySettings) => void
}) => {
  const [edits, setEdits] = useState<UserNotificationSettings>(settings || {})
  const [privacyEdits, setPrivacyEdits] = useState<UserNotificationPrivacySettings>(
    notificationPrivacy || {}
  )
  // Follow the saved values when they change underneath us (e.g. an email
  // unsubscribe arriving over realtime), not just on first render.
  useEffect(() => setEdits(settings || {}), [settings])
  useEffect(() => setPrivacyEdits(notificationPrivacy || {}), [notificationPrivacy])
  const [permissionDenied, setPermissionDenied] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'denied'
  )
  const [showBlockedModal, setShowBlockedModal] = useState(false)

  const updateData = (newEdits: UserNotificationSettings) => {
    setEdits(newEdits)
    onUpdate(newEdits)
  }

  const updatePrivacy = (newEdits: UserNotificationPrivacySettings) => {
    setPrivacyEdits(newEdits)
    onUpdatePrivacy(newEdits)
  }

  // With every delivery channel off the user can't receive anything, so the
  // per-type "Interactions" switches are disabled. Their values are kept
  // (allowDisabledCheck) and apply again once a channel is turned back on.
  const noChannelsEnabled =
    edits.in_app_notifications_enabled !== true &&
    edits.push_notifications_enabled !== true &&
    edits.email_notifications_enabled !== true
  const interactionSwitchProps = {
    disabled: noChannelsEnabled,
    allowDisabledCheck: true,
    disabledText: 'Turn on a notification method above to receive these'
  }

  const handlePushToggle = async (enabled: boolean) => {
    if (!enabled) {
      updateData({ ...edits, push_notifications_enabled: false })
      // Always clean up locally (subscription + service worker), even without a userId
      await unregisterPushToken(userId)
      return
    }
    if (typeof Notification === 'undefined') return
    if (Notification.permission === 'granted') {
      updateData({ ...edits, push_notifications_enabled: true })
      if (userId) await registerPushToken(userId)
      return
    }
    const permission = await Notification.requestPermission()
    if (permission === 'granted') {
      setPermissionDenied(false)
      updateData({ ...edits, push_notifications_enabled: true })
      if (userId) await registerPushToken(userId)
    } else {
      setPermissionDenied(true)
      setShowBlockedModal(true)
      updateData({ ...edits, push_notifications_enabled: false })
    }
  }

  return (
    <>
      <PushBlockedModal open={showBlockedModal} onClose={() => setShowBlockedModal(false)} />
      <VBox px={2.5} pt={2.5} pb={3} gap={3}>
        <SettingsSection title="How I'm notified">
          <FormSwitch
            text="In-app notification"
            description="Show notifications in the notification tray"
            value={edits.in_app_notifications_enabled === true}
            onChange={(in_app_notifications_enabled: boolean) =>
              updateData({ ...edits, in_app_notifications_enabled })
            }
          />
          <VBox>
            <FormSwitch
              text="Push notification"
              description={
                permissionDenied
                  ? 'Notifications were blocked. Re-enable them in your browser settings.'
                  : 'Receive notifications in your browser or on your device'
              }
              value={edits.push_notifications_enabled === true}
              onChange={handlePushToggle}
              disabled={permissionDenied}
            />
            {edits.push_notifications_enabled === true &&
              typeof Notification !== 'undefined' &&
              Notification.permission === 'default' && (
                <TextButton
                  size="small"
                  onClick={() => handlePushToggle(true)}
                  sx={{ alignSelf: 'flex-start', fontSize: 12, p: 0 }}
                >
                  Enable push on this device
                </TextButton>
              )}
          </VBox>
          <FormSwitch
            text="Email notification"
            description="Receive notification by email"
            value={edits.email_notifications_enabled === true}
            onChange={(email_notifications_enabled: boolean) =>
              updateData({ ...edits, email_notifications_enabled })
            }
          />
        </SettingsSection>

        <SettingsSection
          title="Interactions"
          description={
            noChannelsEnabled
              ? "All notification methods are off, so you won't receive these."
              : undefined
          }
        >
          <FormSwitch
            {...interactionSwitchProps}
            text="New followers"
            value={edits.notify_user_followed_you !== false}
            onChange={(notify_user_followed_you: boolean) =>
              updateData({ ...edits, notify_user_followed_you })
            }
          />
          <FormSwitch
            {...interactionSwitchProps}
            text="Actions from people you follow"
            value={edits.notify_friend_took_action !== false}
            onChange={(notify_friend_took_action: boolean) =>
              updateData({ ...edits, notify_friend_took_action })
            }
          />
          <FormSwitch
            {...interactionSwitchProps}
            text="Actions that matched your interests"
            value={edits.notify_new_action_matching_interests !== false}
            onChange={(notify_new_action_matching_interests: boolean) =>
              updateData({ ...edits, notify_new_action_matching_interests })
            }
          />
          <FormSwitch
            {...interactionSwitchProps}
            text="New action in your area"
            value={edits.notify_new_action_in_your_area !== false}
            onChange={(notify_new_action_in_your_area: boolean) =>
              updateData({ ...edits, notify_new_action_in_your_area })
            }
          />
          <FormSwitch
            {...interactionSwitchProps}
            text="You inspired an action"
            description="Notify when someone takes an action after seeing yours."
            value={edits.notify_action_potential_credited !== false}
            onChange={(notify_action_potential_credited: boolean) =>
              updateData({ ...edits, notify_action_potential_credited })
            }
          />
        </SettingsSection>

        <SettingsSection title="Notifications others see">
          <FormSwitch
            text="Completed actions"
            description="Notify followers when you complete an action."
            value={privacyEdits.allow_action_taken_notifications !== false}
            onChange={(allow_action_taken_notifications: boolean) =>
              updatePrivacy({ ...privacyEdits, allow_action_taken_notifications })
            }
          />
          <FormSwitch
            text="Favorited actions"
            description="Notify followers when you favorite an action."
            value={privacyEdits.allow_action_favorited_notifications !== false}
            onChange={(allow_action_favorited_notifications: boolean) =>
              updatePrivacy({ ...privacyEdits, allow_action_favorited_notifications })
            }
          />
        </SettingsSection>

        <NotificationDebugPanel />
      </VBox>
    </>
  )
}

export default UserNotificationEdit
